import * as THREE from 'three/webgpu';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    type InteractionVisualStateRuntime,
    type MaterialSnapshot,
} from '../../manager/interactionState/AbstractInteractionVisualState';
import type { Viewer } from '../../viewer/Viewer';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import { MeasureSelectionMaterialFactory } from '../../material/MeasureSelectionMaterialFactory';
import { PointsMaterialFactory } from '../../material/PointsMaterialFactory';
import { markSharedInteractionMaterial } from '../../material/materialOwnership';

/**
 * 测量选中视觉叠加层。
 *
 * 职责：在底层 TransitionState 刷新完成后，对测量选中集覆写绿色外观。
 *
 * 设计说明：
 * 1. 不替换底层视觉状态类，仅在 refresh 后追加材质覆写；
 * 2. 清除时只恢复本层接管过的对象，避免污染命令态选中视觉。
 */
export class MeasureSelectionVisualOverlay extends AbstractInteractionVisualState {
    /** 当前由测量叠加层接管的对象 ID 集合。 */
    private appliedObjectIds: Set<string> = new Set();

    /** 测量叠加层独占材质快照（与底层状态机快照隔离）。 */
    private readonly overlayMaterialSnapshots: Map<string, MaterialSnapshot> = new Map();

    /**
     * @param runtime 视觉状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
    }

    /**
     * 对测量选中集应用绿色叠加外观。
     *
     * @param selectedObjects 当前测量选中对象列表。
     */
    public applySelectedAppearance(selectedObjects: readonly THREE.Object3D[]): void {
        const nextAppliedIds = new Set<string>();

        selectedObjects.forEach((selectedObject) => {
            this.applyMeasureGreenToObject(selectedObject, nextAppliedIds);

            if (isFeatureFace(selectedObject)) {
                selectedObject.getBoundaryLines().forEach((boundaryLine) => {
                    this.applyMeasureGreenToLine(boundaryLine, nextAppliedIds);
                });
            }
        });

        this.appliedObjectIds.forEach((objectId) => {
            if (nextAppliedIds.has(objectId)) {
                return;
            }
            this.restoreObjectMaterialById(objectId);
        });

        this.appliedObjectIds = nextAppliedIds;
    }

    /**
     * 清除测量叠加外观并恢复已接管材质。
     */
    public clearSelectedAppearance(): void {
        this.appliedObjectIds.forEach((objectId) => {
            this.restoreObjectMaterialById(objectId);
        });
        this.appliedObjectIds.clear();
    }

    /**
     * 对单个对象应用测量选中绿色材质。
     *
     * @param object 目标对象。
     * @param appliedObjectIds 已处理对象 ID 集合。
     */
    private applyMeasureGreenToObject(
        object: THREE.Object3D,
        appliedObjectIds: Set<string>,
    ): void {
        if (appliedObjectIds.has(object.uuid)) {
            return;
        }

        if (isFeatureFace(object)) {
            this.applyMeasureGreenToFace(object, appliedObjectIds);
            return;
        }

        if (object instanceof THREE.Mesh) {
            this.applyMeasureGreenToFace(object, appliedObjectIds);
            return;
        }

        if (isFeatureLine(object) || isDatumAxis(object)) {
            this.applyMeasureGreenToLine(object, appliedObjectIds);
            return;
        }

        if (isFeaturePoint(object) || isDatumPoint(object)) {
            this.applyMeasureGreenToPoint(object, appliedObjectIds);
            return;
        }

        if (isDatumPlaneObject(object)) {
            this.applyMeasureGreenToDatumPlane(object, appliedObjectIds);
        }
    }

    /**
     * 对特征面应用绿色不透明材质。
     *
     * @param faceObject 特征面对象。
     * @param appliedObjectIds 已处理对象 ID 集合。
     */
    private applyMeasureGreenToFace(
        faceObject: THREE.Mesh,
        appliedObjectIds: Set<string>,
    ): void {
        this.snapshotOverlayMaterial(faceObject);
        faceObject.material = this.getMeasureSelectedFaceMaterial();
        appliedObjectIds.add(faceObject.uuid);
        this.runtime.objectStateMachine.set(faceObject.uuid, GeometryInteractionState.Selected);
    }

    /**
     * 对线对象应用绿色不透明线框材质。
     *
     * @param lineObject 线对象。
     * @param appliedObjectIds 已处理对象 ID 集合。
     */
    private applyMeasureGreenToLine(
        lineObject: THREE.Line,
        appliedObjectIds: Set<string>,
    ): void {
        if (appliedObjectIds.has(lineObject.uuid)) {
            return;
        }

        this.snapshotOverlayMaterial(lineObject);
        lineObject.material = this.getMeasureSelectedLineMaterial();
        appliedObjectIds.add(lineObject.uuid);
        this.runtime.objectStateMachine.set(lineObject.uuid, GeometryInteractionState.Selected);
    }

    /**
     * 对点对象应用绿色测量选中材质。
     *
     * @param pointObject 点对象。
     * @param appliedObjectIds 已处理对象 ID 集合。
     */
    private applyMeasureGreenToPoint(
        pointObject: THREE.Sprite,
        appliedObjectIds: Set<string>,
    ): void {
        this.snapshotOverlayMaterial(pointObject);
        pointObject.material = this.getMeasureSelectedPointMaterial();
        appliedObjectIds.add(pointObject.uuid);
        this.runtime.objectStateMachine.set(pointObject.uuid, GeometryInteractionState.Selected);
    }

    /**
     * 对基准面应用绿色边框线框。
     *
     * @param planeObject 基准面对象。
     * @param appliedObjectIds 已处理对象 ID 集合。
     */
    private applyMeasureGreenToDatumPlane(
        planeObject: THREE.Object3D,
        appliedObjectIds: Set<string>,
    ): void {
        planeObject.traverse((childObject) => {
            if (childObject instanceof THREE.Line) {
                this.applyMeasureGreenToLine(childObject, appliedObjectIds);
            }
        });
    }

    /**
     * 按对象 ID 恢复原始材质。
     *
     * @param objectId 对象 UUID。
     */
    private restoreObjectMaterialById(objectId: string): void {
        const snapshot = this.overlayMaterialSnapshots.get(objectId);
        if (!snapshot) {
            this.runtime.objectStateMachine.delete(objectId);
            return;
        }

        snapshot.object.material = snapshot.material;
        this.overlayMaterialSnapshots.delete(objectId);
        this.runtime.objectStateMachine.delete(objectId);
    }

    /**
     * 记录测量叠加层接管前的原始材质。
     *
     * @param object 目标对象。
     */
    private snapshotOverlayMaterial(object: THREE.Mesh | THREE.Line | THREE.Sprite): void {
        if (this.overlayMaterialSnapshots.has(object.uuid)) {
            return;
        }

        this.overlayMaterialSnapshots.set(object.uuid, {
            object,
            material: object.material,
        });
    }

    /**
     * 获取测量选中面材质（工厂单例复用）。
     *
     * @returns 绿色不透明面材质。
     */
    private getMeasureSelectedFaceMaterial(): THREE.MeshStandardMaterial {
        return MeasureSelectionMaterialFactory.getSelectedFaceMaterial();
    }

    /**
     * 获取测量选中线框材质（工厂单例复用）。
     *
     * @returns 绿色不透明线材质。
     */
    private getMeasureSelectedLineMaterial(): THREE.LineBasicMaterial {
        return MeasureSelectionMaterialFactory.getSelectedLineMaterial();
    }

    /**
     * 获取测量选中点材质（工厂单例复用）。
     *
     * @returns 绿色圆形固定像素点材质。
     */
    private getMeasureSelectedPointMaterial(): THREE.SpriteNodeMaterial {
        return markSharedInteractionMaterial(
            PointsMaterialFactory.getMeasureSelectedPointSpriteMaterial(),
        );
    }
}
